import { useEffect, useState } from "react";
import { PhoneCall, PhoneOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const STATUS_LABEL = { queued: "Dialing…", ringing: "Ringing…", "in-progress": "Call in progress", forwarding: "Call in progress" };

// Live view of an outbound inquiry call. Polls until the backend marks the call finished.
export const OutboundCallPanel = ({ inquiry, onFinished }) => {
  const [live, setLive] = useState(inquiry);
  const [ending, setEnding] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/inquiries/${inquiry.id}`);
        if (!alive) return;
        setLive(data);
        if (data.status !== "calling") {
          onFinished(data);
          return;
        }
      } catch { /* keep polling */ }
      if (alive) timer = setTimeout(tick, 3000);
    };
    let timer = setTimeout(tick, 3000);
    const clock = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => { alive = false; clearTimeout(timer); clearInterval(clock); };
  }, [inquiry.id, onFinished]);

  const endCall = async () => {
    setEnding(true);
    try {
      await api.post(`/inquiries/${inquiry.id}/call/end`);
      toast.success("Ending the call…");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not end the call");
      setEnding(false);
    }
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const label = STATUS_LABEL[live.call?.vapi_status] || "Connecting…";

  return (
    <div className="border border-[#34C759]/40 bg-[#34C759]/5 p-6 mb-12 animate-fade-up" data-testid="outbound-call-panel">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="relative flex h-3 w-3 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#34C759] opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#34C759]" />
          </span>
          <div className="min-w-0">
            <p className="font-heading font-black tracking-tight flex items-center gap-2" data-testid="call-status-label">
              <PhoneCall className="w-4 h-4 text-[#34C759]" /> {label}
            </p>
            <p className="text-xs text-muted-foreground font-mono truncate">
              {live.business_name} · {live.call?.to_number} · <span data-testid="call-elapsed">{mm}:{ss}</span>
            </p>
          </div>
        </div>
        <Button type="button" onClick={endCall} disabled={ending} data-testid="end-call-btn"
          className="sm:ml-auto rounded-sm bg-[#FF3B30] hover:bg-[#d62f26] text-white font-semibold gap-2">
          {ending ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneOff className="w-4 h-4" />}
          {ending ? "Ending…" : "End Call"}
        </Button>
      </div>
      {live.call?.questions?.length > 0 && (
        <ol className="mt-5 space-y-1.5 text-sm list-decimal pl-5 text-muted-foreground" data-testid="call-questions">
          {live.call.questions.map((q, i) => <li key={i} className="leading-relaxed">{q}</li>)}
        </ol>
      )}
      <p className="text-[11px] text-muted-foreground mt-4">
        The representative works through the five questions one at a time. The transcript is logged to this inquiry when the call ends,
        and unused minutes from the ${live.call?.hold_amount?.toFixed?.(2)} credit block are refunded.
      </p>
    </div>
  );
};
